import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import { useAppSelector } from '@/redux/hooks';
import { getUrlFromPathname } from '@/redux/modules/config';
import { track } from '@/utils/amplitude/analytics/core';
import {
    PriceResultsMatch,
    QueryIntent,
    SearchCompletedTrigger,
    SearchContext,
    TrackingEvents,
    TrackingPayload,
} from '@/utils/amplitude/analytics/core.types';
import { getAmplitudePageNameFromPath } from '@/utils/getPageNameFromPath';
import { getPageFromQueryParams, parseQueryParams } from '@/utils/queryParams';

export enum TrackSearchCompletedSearchType {
    Auctioneer = 'Auctioneer',
    Keyword = 'Keyword',
}

type TrackSearchCompletedPayload = TrackingPayload & {
    itemScores?: string;
    modifiedQuery?: string;
    normalizedSearchQuery: string;
    numberOfSearchResults: number;
    page: number;
    pageSize: number;
    priceResultsMatch: PriceResultsMatch;
    queryIntent?: QueryIntent;
    searchContext: SearchContext;
    searchQuery: string;
    searchSuggestionUsed: boolean;
    searchType: TrackSearchCompletedSearchType;
    trigger: SearchCompletedTrigger;
};

export const trackSearchCompletedAnalytics = (payload: TrackSearchCompletedPayload) => {
    track(TrackingEvents.Search.SearchCompleted, payload);
};

export const useTrackSearchCompletedAnalytics = () => {
    const { pathname, search } = useLocation();
    const url = useAppSelector((state) => getUrlFromPathname(state, pathname));
    const queryParams = parseQueryParams(search);
    const page = getPageFromQueryParams(queryParams);

    return useCallback(
        ({
            normalizedSearchQuery,
            numberOfSearchResults,
            pageSize,
            priceResultsMatch,
            queryIntent,
            searchContext,
            searchQuery,
            searchSuggestionUsed,
            searchType,
            trigger,
        }: Omit<TrackSearchCompletedPayload, 'pageName' | 'url' | 'page'>) => {
            trackSearchCompletedAnalytics({
                normalizedSearchQuery,
                numberOfSearchResults,
                page,
                pageName: getAmplitudePageNameFromPath(pathname),
                pageSize,
                priceResultsMatch: priceResultsMatch,
                queryIntent,
                searchContext,
                searchQuery,
                searchSuggestionUsed,
                searchType,
                trigger,
                url,
            });
        },
        [page, pathname, url]
    );
};
